import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ClientActions } from '../../components/client-table/client-actions-enum';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ClientCompanyModalComponent } from '../../components/client-company-modal/client-company-modal.component';
import { ClientConfigurationModalComponent } from '../../components/client-configuration-modal/client-configuration-modal.component';
import { ClientDetailPanelsComponent } from '../../components/client-detail-panels/client-detail-panels.component';
import { ClientFormComponent } from '../../components/client-form/client-form.component';
import { Permission } from '@shared/enums/permission.enum';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PermissionService } from '@core/services/permission.service';
import { ClientStore } from '../../client.store';
import {SHARED_IMPORTS} from "../../../../shared/modules/shared-imports.module";
import {ClientConfigPanelsComponent} from '../../components/client-detail-panels/client-config-panels.component';
import {
  ClientCompanyRequest,
  ClientCompanyResponse,
  ClientConfigurationRequest,
  ClientConfigurationResponse,
  ClientRequest,
  ClientResponse,
} from '../../client.model';
import {DetailsPageLayoutComponent} from '@core/layout/page-layouts/details-page-layout/details-page-layout-component';
import {SHARED_UTILS} from '@shared/modules/shared-utils.module';
import { HistoryListComponent } from '@shared/components/history-list/history-list.component'; 

@Component({
  selector: 'app-client-detail-page',
  imports: [RouterLink, ClientDetailPanelsComponent, ClientFormComponent, DetailsPageLayoutComponent, ClientConfigPanelsComponent, HistoryListComponent, ...SharedMaterialUIModule, ...SHARED_IMPORTS],
  templateUrl: './client-detail.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ClientDetailPage {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly permissionService = inject(PermissionService);

  protected readonly clientStore = inject(ClientStore);
  readonly utils = SHARED_UTILS();


  protected readonly clientId = this.route.snapshot.paramMap.get('clientId') ?? '';

  constructor() {
    if (this.clientId) {
      this.clientStore.selectClient(this.clientId);

      const companyId = this.route.snapshot.queryParamMap.get('companyId');
      if (companyId) {
        this.clientStore.filterClientCompanies(this.clientId, companyId);
      }
    }
  }

  protected filterClientCompanies(companyId: string | null): void {
    this.clientStore.filterClientCompanies(this.clientId, companyId);

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { companyId: companyId ?? null },
      queryParamsHandling: 'merge',
    });
  }

  protected saveClient(request: ClientRequest): void {
    if (!this.canUpdate()) {
      return;
    }

    this.clientStore.updateClient(this.clientId, request, () => {
      this.clientStore.selectClient(this.clientId);
    });
  }

  protected toggleClientActive(client: ClientResponse): void {
    if (client.isActive) {
      if (!this.canDeactivate()) {
        return;
      }

      this.clientStore.deactivateClient(this.clientId, () => {
        this.clientStore.selectClient(this.clientId);
      });

      return;
    }

    if (!this.canActivate()) {
      return;
    }

    this.clientStore.activateClient(this.clientId, () => {
      this.clientStore.selectClient(this.clientId);
    });
  }

  protected canUpdate(): boolean {
    return this.permissionService.hasPermission(Permission.ClientsUpdate);
  }

  protected canDeactivate(): boolean {
    return this.permissionService.hasPermission(Permission.ClientsDelete);
  }

  protected canActivate(): boolean {
    return this.permissionService.hasPermission(Permission.ClientsUpdate);
  }

  protected manageConfiguration(event: {
    configuration?: ClientConfigurationResponse;
    action: ClientActions.Create | ClientActions.Edit | ClientActions.Toggled;
  }): void {
    if (!this.canUpdate()) {
      return;
    }

    if (event.action === ClientActions.Toggled && event.configuration) {
      if (event.configuration.isActive) {
        this.clientStore.deactivateConfiguration(
          event.configuration.id,
          this.clientId,
          () => {}
        );
      } else {
        this.clientStore.activateConfiguration(
          event.configuration.id,
          this.clientId,
          () => {}
        );
      }

      return;
    }

    const dialogRef = this.dialog.open(ClientConfigurationModalComponent, {
      width: '600px',
      maxWidth: '95vw',
    });

    dialogRef.componentRef?.setInput('clientId', this.clientId);
    dialogRef.componentRef?.setInput(
      'initialValue',
      event.configuration ?? null
    );

    dialogRef.afterClosed().subscribe(
      (result: ClientConfigurationRequest | undefined) => {
        if (!result) {
          return;
        }

        if (event.action === ClientActions.Create) {
          this.clientStore.createConfiguration(result, () => {});
        } else if (event.action === ClientActions.Edit && event.configuration) {
          this.clientStore.updateConfiguration(
            event.configuration.id,
            result,
            () => {}
          );
        }
      }
    );
  }
    protected manageClientCompany(event: {
    data?: ClientCompanyResponse;
    action: ClientActions.Create | ClientActions.Edit | ClientActions.Toggled;
  }): void {
    if (!this.canUpdate()) {
      return;
    }

    if (event.action === ClientActions.Toggled && event.data) {
      if (event.data.isActive) {
        this.clientStore.deactivateClientCompany(
          event.data.id,
          this.clientId,
          () => {}
        );
      } else {
        this.clientStore.activateClientCompany(
          event.data.id,
          this.clientId,
          () => {}
        );
      }

      return;
    }

    const dialogRef = this.dialog.open(ClientCompanyModalComponent, {
      width: '600px',
      maxWidth: '95vw',
    });

    dialogRef.componentRef?.setInput('clientId', this.clientId);
    dialogRef.componentRef?.setInput(
      'companies',
      this.clientStore.companies()
    );
    dialogRef.componentRef?.setInput(
      'initialValue',
      event.data ?? null
    );

    dialogRef.afterClosed().subscribe(
      (result: ClientCompanyRequest | undefined) => {
        if (!result) {
          return;
        }

        if (event.action === 'create') {
          this.clientStore.createClientCompany(result, () => {});
        } else if (event.action === 'edit' && event.data) {
          this.clientStore.updateClientCompany(
            event.data.id,
            result,
            () => {}
          );
        }
      }
    );
  }
}